import type { InlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { GetResourceEventsResponse, KubernetesLiveStatusDetailedResource, Repository } from "@octopusdeploy/octopus-server-client";
import moment, { type Moment } from "moment/moment";
import { useCallback, useState } from "react";
import { delay } from "~/utils/delay";
type UseEventsQueryReturnValue = InlineStatusQuery<GetResourceEventsResponse | undefined | null, never> & {
    requestCompletedTime: Moment;
};
export function useEventsQuery(detailedResource: KubernetesLiveStatusDetailedResource | undefined, projectId: string, environmentId: string, tenantId: string | undefined): UseEventsQueryReturnValue {
    const [requestCompletedTime, setRequestCompletedTime] = useState<Moment>(moment());
    const kubernetesMonitorQuery = useCallback(async (repository: Repository, abortSignal: AbortSignal, detailedResource: KubernetesLiveStatusDetailedResource) => {
        const id = detailedResource.DesiredResourceId || detailedResource.ResourceId;
        if (!id) {
            return Promise.reject("The live resource does not have a DesiredResourceId or ResourceId");
        }
        const { SessionId } = await repository.KubernetesLiveStatus.beginResourceEventsSession(projectId, environmentId, tenantId, detailedResource.ResourceSourceId, id);
        //while this is not aborted, continue attempting to load
        while (!abortSignal.aborted) {
            const result = await repository.KubernetesLiveStatus.getResourceEvents(SessionId);
            if (result.IsSessionCompleted) {
                setRequestCompletedTime(moment());
                return result;
            }
            await delay(100, abortSignal);
        }
    }, [environmentId, projectId, tenantId]);
    const argoCDQuery = useCallback(async (repository: Repository, detailedResource: KubernetesLiveStatusDetailedResource) => {
        const id = detailedResource.DesiredResourceId || detailedResource.ResourceId;
        if (!id) {
            return Promise.reject("The live resource does not have a DesiredResourceId or ResourceId");
        }
        const response = await repository.KubernetesLiveStatus.getArgoCDResourceEvents(projectId, environmentId, tenantId, detailedResource.ResourceSourceId, detailedResource.SourceType, id);
        setRequestCompletedTime(moment());
        return response;
    }, [environmentId, projectId, tenantId]);
    const result = useInlineStatusQuery(async (repo, abortSignal) => {
        if (!detailedResource) {
            return null;
        }
        if (detailedResource.SourceType === "KubernetesMonitor") {
            return await kubernetesMonitorQuery(repo, abortSignal, detailedResource);
        }
        else {
            return await argoCDQuery(repo, detailedResource);
        }
    }, [argoCDQuery, detailedResource, kubernetesMonitorQuery], "Load events for live object");
    return {
        ...result,
        requestCompletedTime,
    };
}
